<div class="h-full flex flex-col overflow-hidden relative"
     [ngClass]="darkMode ? 'theme-dark bg-gray-950 text-slate-200' : 'theme-light bg-white text-gray-800'">

  <!-- ═══ HEADER — aligned with left-nav badge ═══ -->
  <div class="shrink-0 pt-3 px-3">
    <div class="h-14 pl-[60px] pr-3 flex items-center justify-between gap-3 rounded-xl"
         [ngClass]="darkMode ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-gray-50 border border-gray-100'">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <h1 class="text-sm font-semibold truncate">Project access</h1>
          <span class="text-[11px] font-medium px-1.5 py-0.5 rounded-md tabular-nums"
                [ngClass]="darkMode ? 'bg-white/[0.06] text-slate-400' : 'bg-gray-200/70 text-gray-500'">{{ members.length }}</span>
        </div>
        <p class="text-[11px] truncate" [ngClass]="darkMode ? 'text-slate-400' : 'text-gray-500'">
          Who can edit <span class="font-medium" [ngClass]="darkMode ? 'text-slate-300' : 'text-gray-700'">{{ projectName }}</span>
        </p>
      </div>
      <button *ngIf="amOwner" (click)="openAdd()"
              class="shrink-0 px-3.5 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-all duration-200 hover:opacity-90 hover:scale-[1.02] shadow-lg shadow-amber-900/20 bg-gradient-to-r from-amber-500 to-orange-500">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        Add member
      </button>
    </div>
  </div>

  <!-- ═══ MEMBERS ═══ -->
  <div class="flex-1 overflow-auto custom-scroll px-3 py-4">
    <div class="max-w-2xl mx-auto">

      <!-- Loading -->
      <div *ngIf="loading" class="space-y-2">
        <div *ngFor="let i of [1,2,3]" class="h-[60px] rounded-xl animate-pulse"
             [ngClass]="darkMode ? 'bg-white/[0.03]' : 'bg-gray-100'"></div>
      </div>

      <!-- List -->
      <div *ngIf="!loading" class="space-y-2">
        <div *ngFor="let m of members" class="space-y-1">
          <div class="group flex items-center gap-3.5 px-3.5 py-3 rounded-xl border transition-all duration-200"
             [ngClass]="darkMode ? 'border-white/[0.07] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]' : 'border-gray-100 bg-white hover:bg-gray-50 hover:border-gray-200 shadow-sm'">

          <!-- Avatar (tinted by role) -->
          <div class="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ring-1"
               [ngClass]="{
                 'bg-amber-500/15 text-amber-500 ring-amber-500/30': m.access_level === 'owner',
                 'bg-blue-500/15 text-blue-500 ring-blue-500/30': m.access_level === 'editor',
                 'bg-slate-400/15 text-slate-400 ring-slate-400/25': m.access_level === 'viewer'
               }">{{ initials(m) }}</div>

          <!-- Identity -->
          <div class="min-w-0 flex-1">
            <div class="text-sm font-medium truncate flex items-center gap-1.5">
              {{ m.name || (m.email || m.accountId) }}
              <span *ngIf="m.zavoTeam"
                    class="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/15 text-purple-400 ring-1 ring-purple-500/30">Zavo team</span>
              <span *ngIf="isMe(m)" class="text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0"
                    [ngClass]="darkMode ? 'bg-white/[0.08] text-slate-400' : 'bg-gray-100 text-gray-500'">you</span>
            </div>
            <div class="text-xs truncate" [ngClass]="darkMode ? 'text-slate-400' : 'text-gray-500'">{{ m.email || '—' }}</div>
          </div>

          <!-- Access level. Owners get a real control; everyone else gets the same pill, read-only.
               The pill IS the trigger, so the badge never changes shape based on who is looking. -->
          <div class="relative shrink-0">

            <button *ngIf="amOwner" type="button"
                    (click)="toggleMenu(m, $event)"
                    aria-haspopup="listbox"
                    [attr.aria-expanded]="openMenuFor === m.accountId"
                    [attr.aria-label]="'Change access level for ' + (m.email || m.accountId)"
                    class="text-[11px] font-semibold pl-2.5 pr-1.5 py-1 rounded-full capitalize flex items-center gap-1 ring-1 ring-inset transition-all cursor-pointer"
                    [ngClass]="{
                      'bg-amber-500/15 text-amber-500 ring-amber-500/30 hover:bg-amber-500/25': m.access_level === 'owner',
                      'bg-blue-500/15 text-blue-500 ring-blue-500/30 hover:bg-blue-500/25': m.access_level === 'editor',
                      'bg-slate-400/15 text-slate-500 ring-slate-400/25 hover:bg-slate-400/25': m.access_level === 'viewer'
                    }">
              <svg *ngIf="m.access_level === 'owner'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
              {{ m.access_level }}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3 transition-transform duration-200" [class.rotate-180]="openMenuFor === m.accountId"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>

            <span *ngIf="!amOwner"
                  class="text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize flex items-center gap-1 ring-1 ring-inset"
                  [ngClass]="{
                    'bg-amber-500/15 text-amber-500 ring-amber-500/30': m.access_level === 'owner',
                    'bg-blue-500/15 text-blue-500 ring-blue-500/30': m.access_level === 'editor',
                    'bg-slate-400/15 text-slate-500 ring-slate-400/25': m.access_level === 'viewer'
                  }">
              <svg *ngIf="m.access_level === 'owner'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
              {{ m.access_level }}
            </span>

            <!-- Level menu. Disabled options keep their row and explain themselves rather than
                 disappearing, so the last-owner rule is discoverable instead of mysterious. -->
            <div *ngIf="openMenuFor === m.accountId" (click)="$event.stopPropagation()" role="listbox"
                 class="absolute right-0 top-full mt-1.5 w-64 z-50 rounded-xl border shadow-2xl overflow-hidden"
                 [ngClass]="darkMode ? 'bg-[#0d0d18] border-white/[0.12]' : 'bg-white border-gray-200'"
                 style="animation: umModalPop 0.16s cubic-bezier(0.16,1,0.3,1)">
              <button *ngFor="let lvl of LEVELS" type="button" role="option"
                      [attr.aria-selected]="m.access_level === lvl.id"
                      [disabled]="!!levelDisabledReason(m, lvl.id)"
                      (click)="changeLevel(m, lvl.id)"
                      class="w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors disabled:cursor-not-allowed"
                      [ngClass]="[
                        levelDisabledReason(m, lvl.id) ? 'opacity-45' : (darkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'),
                        m.access_level === lvl.id ? (darkMode ? 'bg-white/[0.04]' : 'bg-gray-50/70') : ''
                      ]">
                <svg *ngIf="m.access_level === lvl.id" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500"><polyline points="20 6 9 17 4 12"></polyline></svg>
                <span *ngIf="m.access_level !== lvl.id" class="w-3.5 shrink-0"></span>
                <span class="min-w-0">
                  <span class="text-xs font-semibold flex items-center gap-1"
                        [ngClass]="darkMode ? 'text-slate-200' : 'text-gray-800'">
                    <svg *ngIf="lvl.id === 'owner'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 text-amber-500"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
                    {{ lvl.label }}
                  </span>
                  <span class="block text-[10px] leading-snug mt-0.5"
                        [ngClass]="levelDisabledReason(m, lvl.id)
                          ? 'text-amber-500'
                          : (darkMode ? 'text-slate-500' : 'text-gray-500')">
                    {{ levelDisabledReason(m, lvl.id) || lvl.blurb }}
                  </span>
                </span>
              </button>
            </div>
          </div>

          <!-- The builder's role INSIDE the app — the same one in the editor, Developer mode, Preview and Live. -->
          <div class="flex items-center gap-2 mt-1">
            <span class="text-[11px] text-gray-400">In the app</span>
            <select *ngIf="canSetAppRole(m)"
                    [disabled]="savingRole === m.accountId"
                    (change)="changeAppRole(m, $any($event.target).value)"
                    class="px-2 py-1 text-xs rounded border border-gray-200 bg-white">
              <option *ngFor="let r of appRoles" [value]="r.id" [selected]="(m.app_role_id || 'admin') === r.id">{{ r.name }}</option>
              <!-- Only an owner GRANTS "No access", but someone already set to it must see it on their own
                   row: with the option hidden, nothing matched and the browser showed the first role
                   instead, so a locked-out builder read "Admin" while the app refused them. -->
              <option *ngIf="(amOwner && !isMe(m)) || m.app_role_id === 'inactive'"
                      value="inactive"
                      [disabled]="!(amOwner && !isMe(m))"
                      [selected]="m.app_role_id === 'inactive'">No access</option>
            </select>
            <span *ngIf="!canSetAppRole(m)" class="text-xs text-gray-500">{{ m.app_role_id || 'admin' }}</span>
          </div>

          <!-- Remove -->
          <button *ngIf="canRemove(m)" (click)="remove(m)" title="Remove member"
                  class="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  [ngClass]="darkMode ? 'text-slate-500 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <!-- Rejected mutation, attached to the row that failed — never fail silently, and never
             make the reader guess which member the message is about. -->
        <div *ngIf="rowError && rowErrorAccountId === m.accountId"
             class="text-[11px] px-3 py-2 -mt-1 rounded-lg flex items-center gap-1.5"
             [ngClass]="darkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 shrink-0"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
          {{ rowError }}
          </div>
        </div>

        <!-- Empty state -->
        <div *ngIf="members.length === 0" class="flex flex-col items-center justify-center text-center py-16">
          <div class="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
               [ngClass]="darkMode ? 'bg-white/[0.04] text-slate-500' : 'bg-gray-100 text-gray-400'">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-6 h-6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
          </div>
          <p class="text-sm font-medium" [ngClass]="darkMode ? 'text-slate-300' : 'text-gray-600'">No members yet</p>
          <p class="text-xs mt-1" [ngClass]="darkMode ? 'text-slate-500' : 'text-gray-400'">Add a teammate by their builder code to collaborate.</p>
        </div>
      </div>

      <p *ngIf="roleError" class="text-xs text-red-600 mt-2">{{ roleError }}</p>
    </div>
  </div>
</div>

<!-- ═══ Add-by-code modal ═══ -->
<div *ngIf="showAdd" class="fixed inset-0 z-[1000] flex items-center justify-center p-4">
  <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="closeAdd()"></div>
  <div class="relative w-[440px] max-w-full rounded-2xl shadow-2xl border overflow-hidden"
       [ngClass]="darkMode ? 'bg-[#0d0d18] border-white/[0.1] text-slate-200' : 'bg-white border-gray-100 text-gray-800'"
       style="animation: umModalPop 0.25s cubic-bezier(0.16,1,0.3,1)">

    <!-- header -->
    <div class="flex items-center justify-between px-5 py-4 border-b" [ngClass]="darkMode ? 'border-white/[0.08]' : 'border-gray-100'">
      <span class="text-sm font-semibold" [ngClass]="darkMode ? 'text-white' : 'text-gray-900'">Add member</span>
      <button (click)="closeAdd()" class="transition-colors" [ngClass]="darkMode ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    </div>

    <div class="px-5 py-5 space-y-4">
      <div>
        <label class="block text-xs font-medium mb-1.5" [ngClass]="darkMode ? 'text-slate-400' : 'text-gray-500'">Builder code</label>
        <p class="text-[11px] mb-2" [ngClass]="darkMode ? 'text-slate-500' : 'text-gray-400'">Ask the person for their code (from their profile → Settings).</p>
        <div class="flex gap-2">
          <input [(ngModel)]="code" (ngModelChange)="resolved = null" (keyup.enter)="resolve()" placeholder="e.g. ABC123WXYZ"
                 class="flex-1 px-3 py-2 rounded-lg border text-sm font-mono tracking-wider uppercase focus:outline-none transition-colors"
                 [ngClass]="darkMode ? 'bg-white/[0.04] border-white/[0.1] text-white placeholder-slate-600 focus:border-amber-400/60' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-amber-400'" />
          <button (click)="resolve()" [disabled]="!code.trim() || resolving"
                  class="px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40"
                  [ngClass]="darkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-900 text-white hover:bg-gray-700'">
            {{ resolving ? '…' : 'Look up' }}
          </button>
        </div>
        <p *ngIf="addError" class="text-[11px] text-red-500 mt-1.5">{{ addError }}</p>
      </div>

      <div *ngIf="resolved" class="rounded-xl border p-3.5 space-y-3"
           [ngClass]="darkMode ? 'border-white/[0.08] bg-white/[0.02]' : 'border-gray-100 bg-gray-50/50'">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 bg-amber-500/15 text-amber-500 ring-1 ring-amber-500/30">
            {{ (resolved.name || resolved.email || '?').trim().slice(0,2).toUpperCase() }}
          </div>
          <div class="min-w-0">
            <div class="text-sm font-medium truncate" [ngClass]="darkMode ? 'text-white' : 'text-gray-900'">{{ resolved.name || resolved.email }}</div>
            <div class="text-xs truncate" [ngClass]="darkMode ? 'text-slate-400' : 'text-gray-500'">{{ resolved.email }}</div>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium mb-1.5" [ngClass]="darkMode ? 'text-slate-400' : 'text-gray-500'">Access</label>
          <div class="flex gap-2">
            <label *ngFor="let lvl of ['owner','editor','viewer']"
                   class="flex-1 text-center text-xs font-semibold px-2 py-1.5 rounded-lg border cursor-pointer capitalize transition-colors"
                   [ngClass]="chosenLevel === lvl
                     ? 'border-amber-400 bg-amber-500/10 text-amber-500'
                     : (darkMode ? 'border-white/[0.1] text-slate-400 hover:bg-white/[0.04]' : 'border-gray-200 text-gray-500 hover:bg-gray-50')">
              <input type="radio" [value]="lvl" [(ngModel)]="chosenLevel" class="hidden" /> {{ lvl }}
            </label>
          </div>
          <p class="text-[11px] mt-1.5" [ngClass]="darkMode ? 'text-slate-500' : 'text-gray-400'">A project can have only one owner.</p>
        </div>
        <button data-mcp-button="Add to project" (click)="add()" class="w-full px-3 py-2 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 shadow-lg shadow-amber-900/20 bg-gradient-to-r from-amber-500 to-orange-500">Add to project</button>
      </div>
    </div>
  </div>
</div>
